import { ReturnsProcessingItemStatus } from "@redotech/cockroachdb-codegen/tables/returns/returns-processing-item-status";
import { accountIdFormat } from "@redotech/redo-account-id";
import {
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import {
  processingSessionIdFormat,
  processingSessionItemIdFormat,
} from "@redotech/redo-return-id";
import {
  processingEventTypes,
  returnsProcessingSessionType,
} from "@redotech/redo-return-warehouse-processing-db/processing-types";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  ReturnsProcessingSessionType,
  returnsProcessingSessionType,
} from "@redotech/redo-return-warehouse-processing-db/processing-types";

export const SessionStatus = ["active", "completed", "abandoned"] as const;
export type SessionStatus = (typeof SessionStatus)[number];

export const ItemProcessingStatusSchema = createEnum(
  ReturnsProcessingItemStatus,
);
export type ItemProcessingStatus = z.infer<typeof ItemProcessingStatusSchema>;

const OpenItemReviewFailure_ = z.object({
  reason: z.string().nullable(),
  note: z.string().nullable(),
  flaggedByName: z.string().nullable(),
  flaggedAt: z.date(),
});
export const OpenItemReviewFailure: z.ZodType<OpenItemReviewFailure> =
  OpenItemReviewFailure_;
export interface OpenItemReviewFailure extends z.infer<
  typeof OpenItemReviewFailure_
> {}

const ProcessingSessionItem_ = z.object({
  id: processingSessionItemIdFormat.schema,
  sessionId: processingSessionIdFormat.schema,
  returnItemId: z.string().nullable(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  status: ItemProcessingStatusSchema,
  grade: z.string().nullable(),
  outcome: z.string().nullable(),
  notes: z.string().nullable(),
  productTitle: z.string().nullable(),
  productSku: z.string().nullable(),
  productImageUrl: z.string().nullable(),
  lotNumber: z.string().nullable(),
  expirationDate: z.date().nullable(),
  isLotTracked: z.boolean(),
  openItemReviewFailures: z.array(OpenItemReviewFailure_),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ProcessingSessionItem: z.ZodType<ProcessingSessionItem> =
  ProcessingSessionItem_;
export interface ProcessingSessionItem extends z.infer<
  typeof ProcessingSessionItem_
> {}

/**
 * RPC-facing projection of a returns_processing_event row. The full event log
 * for a session is returned with each session response so the client can
 * derive UI signals (variant mismatch, agent-eval outcome, etc.) without
 * adding a bespoke schema field per signal.
 *
 * `data` is intentionally typed as Record<string, unknown> — the shape is
 * specific to the event/step type and lives in the corresponding step handler.
 * Consumers should narrow at the use site.
 */
const ProcessingEvent_ = z.object({
  sessionItemId: processingSessionItemIdFormat.schema.nullable(),
  type: z.enum(processingEventTypes),
  data: z.record(z.string(), z.unknown()),
  createdAt: z.date(),
});
export const ProcessingEvent: z.ZodType<ProcessingEvent> = ProcessingEvent_;
export interface ProcessingEvent extends z.infer<typeof ProcessingEvent_> {}

export const ReturnsProcessingSessionTypeSchema = z.enum(
  returnsProcessingSessionType,
);

export const ProcessingSession_ = z.object({
  id: processingSessionIdFormat.schema,
  accountId: accountIdFormat.schema,
  returnId: z.string(),
  legacyUserId: z.string(),
  type: ReturnsProcessingSessionTypeSchema.nullable(),
  locationId: locationIdFormat.schema.nullable(),
  status: z.enum(SessionStatus),
  items: z.array(ProcessingSessionItem_),
  events: z.array(ProcessingEvent_),
  createdAt: z.date(),
  updatedAt: z.date(),
  completedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
});
export const ProcessingSession: z.ZodType<ProcessingSession> =
  ProcessingSession_;
export interface ProcessingSession extends z.infer<typeof ProcessingSession_> {}

export function deriveSessionStatus(session: {
  completedAt: Date | null;
  deletedAt: Date | null;
}): SessionStatus {
  if (session.deletedAt) {
    return "abandoned";
  }
  if (session.completedAt) {
    return "completed";
  }
  return "active";
}
